import type { CaseInconsistency } from "@/services/api/case-checking";

/** Matches backend `collect_assessment_rule_results` / problem-statement roll-up. */
export type InconsistencyRollupStatus = "pass" | "fail" | "unclear" | "na";

const NOT_APPLICABLE_MESSAGE =
  /applies (?:only )?to .* transaction|not an? .+ transaction|check does not apply|does not involve a|not a switch|not a replacement|no switch|no replacement|not the nature of this advice|does not apply to this|not applicable to this/i;

export function inconsistencyRollupStatus(row: CaseInconsistency): InconsistencyRollupStatus {
  const statusRaw = (row.status ?? "").toLowerCase().trim().replace(/_/g, "-");
  if (statusRaw === "pass" || statusRaw === "passed") return "pass";
  if (["na", "n/a", "not-applicable"].includes(statusRaw)) return "na";
  if (["unclear", "cannot-assess", "review"].includes(statusRaw)) {
    if (NOT_APPLICABLE_MESSAGE.test(row.message ?? "")) return "na";
    return "unclear";
  }
  if (
    ["fail", "failed", "flagged"].includes(statusRaw) ||
    row.severity === "high" ||
    row.severity === "medium"
  ) {
    return "fail";
  }
  return "unclear";
}

export function isChronologyCheckId(checkId: string | null | undefined): boolean {
  return Boolean(checkId?.toLowerCase().startsWith("chron-"));
}

export function isConsistencyCheckId(checkId: string | null | undefined): boolean {
  const id = (checkId ?? "").toLowerCase();
  return id.startsWith("cons-") || id.startsWith("c");
}

export const INCONSISTENCY_ROLLUP_DOT: Record<InconsistencyRollupStatus, string> = {
  pass: "bg-emerald-500",
  fail: "bg-red-500",
  unclear: "bg-amber-500",
  na: "bg-slate-400",
};

export const INCONSISTENCY_ROLLUP_BADGE: Record<InconsistencyRollupStatus, string> = {
  pass: "bg-emerald-500/15 text-emerald-300 ring-emerald-500/30",
  fail: "bg-red-500/15 text-red-300 ring-red-500/30",
  unclear: "bg-amber-500/15 text-amber-300 ring-amber-500/30",
  na: "bg-slate-500/15 text-slate-300 ring-slate-500/30",
};

export const INCONSISTENCY_ROLLUP_LABEL: Record<InconsistencyRollupStatus, string> = {
  pass: "Pass",
  fail: "Fail",
  unclear: "Unclear",
  na: "N/A",
};

export function countInconsistencyRollups(rows: CaseInconsistency[]): {
  fail: number;
  unclear: number;
  pass: number;
} {
  const counts = { fail: 0, unclear: 0, pass: 0 };
  for (const row of rows) {
    const status = inconsistencyRollupStatus(row);
    if (status === "fail") counts.fail += 1;
    else if (status === "unclear") counts.unclear += 1;
    else if (status === "pass") counts.pass += 1;
  }
  return counts;
}
